import { useEffect, useState } from "react";

interface TopTimerBarProps {
  totalMs: number;
  startedAt: number;
  onComplete?: () => void;
  /** When true, the bar stops at its current width (no more shrinking). */
  paused?: boolean;
}

/**
 * Thin horizontal countdown bar fixed to the top of the viewport. The bar
 * starts full-width amber, shrinks proportionally as time runs out, and
 * turns red in the last 3 seconds. When time hits zero the bar fades out
 * and calls `onComplete`.
 */
export function TopTimerBar({
  totalMs,
  startedAt,
  onComplete,
  paused,
}: TopTimerBarProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [paused]);

  const elapsed = Math.max(0, now - startedAt);
  const remaining = paused ? 0 : Math.max(0, totalMs - elapsed);
  const ratio = totalMs > 0 ? remaining / totalMs : 0;
  const urgent = !paused && remaining > 0 && remaining < 3000;

  useEffect(() => {
    if (!paused && remaining === 0 && onComplete) onComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining === 0, paused]);

  return (
    <div
      className="fixed top-0 left-0 right-0 h-1.5 bg-brand-ink2 z-40 pointer-events-none"
      aria-hidden="true"
    >
      <div
        className={`h-full ${urgent ? "bg-brand-bad" : "bg-brand-amber"}`}
        style={{
          width: `${Math.max(0, ratio * 100)}%`,
          transition: "width 100ms linear, background-color 200ms ease",
          opacity: remaining === 0 ? 0 : 1,
        }}
      />
    </div>
  );
}
